import styled$1, { createGlobalStyle as createGlobalStyle$1, ThemeContext, ThemeProvider, css as css$1 } from 'styled-components';
export { ServerStyleSheet, StyleSheetManager, ThemeConsumer, ThemeContext, ThemeProvider, isStyledComponent, keyframes, withTheme } from 'styled-components';
import { createColorStyles, createColorModeProvider, createUseGetter, useThemeScreens, useThemeBreakpoint, useThemeUp, useThemeDown, createTransform } from '@xstyled/core';
export { getColorModeInitScriptElement, getColorModeInitScriptTag, useColorMode, useViewportWidth } from '@xstyled/core';
import { useContext } from 'react';
import { th, getPreflightStyles, system as system$1 } from '@xstyled/system';
export * from '@xstyled/system';
import { flattenStrings, string } from '@xstyled/util';

const ColorModeStyle = createGlobalStyle$1`${(p) => createColorStyles(p.theme, { targetSelector: p.targetSelector })}`;
const ColorModeProvider = createColorModeProvider({
  ThemeContext,
  ThemeProvider,
  ColorModeStyle
});

const useTheme = () => {
  return useContext(ThemeContext);
};
const useTh = createUseGetter(th, useTheme);
const useAngle = createUseGetter(th.angle, useTheme);
const useAnimation = createUseGetter(th.animation, useTheme);
const useBorder = createUseGetter(th.border, useTheme);
const useBorderColor = createUseGetter(th.borderColor, useTheme);
const useBorderStyle = createUseGetter(th.borderStyle, useTheme);
const useBorderWidth = createUseGetter(th.borderWidth, useTheme);
const useColor = createUseGetter(th.color, useTheme);
const useDuration = createUseGetter(th.duration, useTheme);
const useFont = createUseGetter(th.font, useTheme);
const useFontSize = createUseGetter(th.fontSize, useTheme);
const useFontWeight = createUseGetter(th.fontWeight, useTheme);
const useInset = createUseGetter(th.inset, useTheme);
const useLetterSpacing = createUseGetter(th.letterSpacing, useTheme);
const useLineHeight = createUseGetter(th.lineHeight, useTheme);
const usePercent = createUseGetter(th.percent, useTheme);
const usePx = createUseGetter(th.px, useTheme);
const useRadius = createUseGetter(th.radius, useTheme);
const useRingWidth = createUseGetter(th.ringWidth, useTheme);
const useShadow = createUseGetter(th.shadow, useTheme);
const useSize = createUseGetter(th.size, useTheme);
const useSpace = createUseGetter(th.space, useTheme);
const useTimingFunction = createUseGetter(th.timingFunction, useTheme);
const useTransform = createUseGetter(th.transform, useTheme);
const useTransition = createUseGetter(th.transition, useTheme);
const useTransitionProperty = createUseGetter(
  th.transitionProperty,
  useTheme
);
const useZIndex = createUseGetter(th.zIndex, useTheme);

const useScreens = () => {
  return useThemeScreens(useTheme());
};
const useBreakpoint = () => {
  return useThemeBreakpoint(useTheme());
};
const useUp = (key) => {
  return useThemeUp(useTheme(), key);
};
const useDown = (key) => {
  return useThemeDown(useTheme(), key);
};

const Preflight = createGlobalStyle$1(
  ({ theme }) => getPreflightStyles(theme)
);

const createCssFunction = (generator) => {
  const transform = createTransform(generator);
  return (...args) => {
    const scCssArgs = css$1(...args);
    const flattenedArgs = flattenStrings(scCssArgs);
    return flattenedArgs.map(transform);
  };
};

const scStyled = typeof styled$1 === "function" ? styled$1 : styled$1.default;

const getCreateStyle = (baseCreateStyle, css, generator) => {
  const createStyle = (...args) => baseCreateStyle`${css(...args)}${generator}`;
  createStyle.attrs = (attrs) => getCreateStyle(baseCreateStyle.attrs(attrs), css, generator);
  createStyle.withConfig = (config) => getCreateStyle(baseCreateStyle.withConfig(config), css, generator);
  return createStyle;
};
const createShouldForwardProp = (generator) => {
  const propSet = new Set(generator.meta.props);
  return (prop, defaultValidatorFn, elementToBeCreated) => {
    if (string(prop) && propSet.has(prop)) {
      return false;
    }
    if (typeof elementToBeCreated === "string") {
      return defaultValidatorFn(prop);
    }
    return true;
  };
};
const createBaseStyled = (css, generator) => {
  const config = generator ? {
    shouldForwardProp: createShouldForwardProp(generator)
  } : {};
  return (component) => {
    const baseStyled = scStyled(component);
    return getCreateStyle(
      config ? baseStyled.withConfig(config) : baseStyled,
      css,
      generator
    );
  };
};
const createStyled = (generator) => {
  const css = createCssFunction(generator);
  const styled = createBaseStyled(css);
  const xstyled = createBaseStyled(css, generator);
  styled.box = xstyled("div");
  Object.keys(scStyled).forEach((key) => {
    styled[key] = styled(key);
    styled[`${key}Box`] = xstyled(key);
  });
  return styled;
};

const createX = (generator) => {
  const xstyled = createBaseStyled(createCssFunction(generator), generator);
  const x = {};
  Object.keys(scStyled).forEach((tag) => {
    x[tag] = xstyled(tag)``;
  });
  return x;
};

const createCreateGlobalStyle = (generator) => {
  const css = createCssFunction(generator);
  return (...args) => createGlobalStyle$1([
    css(...args)
  ]);
};

const createCss = (generator) => {
  return {
    css: createCssFunction(generator),
    x: createX(generator),
    styled: createStyled(generator),
    createGlobalStyle: createCreateGlobalStyle(generator)
  };
};

const { css, styled, x, createGlobalStyle } = createCss(system$1);

export { ColorModeProvider, Preflight, createCss, createGlobalStyle, css, styled as default, styled, useAngle, useAnimation, useBorder, useBorderColor, useBorderStyle, useBorderWidth, useBreakpoint, useColor, useDown, useDuration, useFont, useFontSize, useFontWeight, useInset, useLetterSpacing, useLineHeight, usePercent, usePx, useRadius, useRingWidth, useScreens, useShadow, useSize, useSpace, useTh, useTheme, useTimingFunction, useTransform, useTransition, useTransitionProperty, useUp, useZIndex, x };
//# sourceMappingURL=index.mjs.map
